import React, { useState, useRef, useMemo, useEffect } from 'react';
import JoditEditor from 'jodit-react';
import axios from 'axios';
import {
  Layout,
  Form,
  Input,
  Button,
  DatePicker,
  Upload,
  Table,
  Space,
  Typography,
  Card,
  Tabs,
  Collapse,
  Popconfirm,
  message,
  Divider,
  Badge,
  Tooltip,
  Row,
  Col,
  Dropdown,
  Menu,
  Select
} from 'antd';
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  SearchOutlined,
  SaveOutlined,
  FileImageOutlined,
  ReloadOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  MoreOutlined
} from '@ant-design/icons';
// Using dayjs instead of moment as it's the recommended date library for Ant Design
import dayjs from 'dayjs';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

interface Blog {
  placeholder?: string;
  _id?: string;
}

interface BlogData {
  blogs: {
    _id: string;
    title: string;
    fullTitle: string;
    description: string;
    date: string;
    researchAssociate: string;
    image: string | null;
    content: string;
    meta: string;
    key: string;
    slug: string;
    altTag: string;
    Canonical?: string;
    faq: { question: string, answer: string }[];
    // Backend uses `published`; keep legacy `isPublished` for backward compatibility
    published?: boolean;
    isPublished?: boolean;
    userPostDataInfo?: Array<{
      name: string;
      role: string;
      emailId: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }[];
}

const BlogManagement: React.FC<Blog> = ({ placeholder }) => {
  const editor = useRef(null);
  const [form] = Form.useForm();
  const [content, setContent] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [data, setData] = useState<BlogData>({ blogs: [] });
  const [faq, setFaq] = useState<{ question: string; answer: string }[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  //   const [previewContent, setPreviewContent] = useState<string>('');
  //   const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState<string>('1');
  const [isPublished, setIsPublished] = useState<boolean>(false); // local form state; will map to `published`
  const [publishFilter, setPublishFilter] = useState<string>("all"); // "all", "published", "unpublished"

  // Get user role from localStorage
  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  // Jodit Editor configuration
  const config = useMemo(() => ({
    readonly: false,
    placeholder: placeholder || 'Start writing your blog content...',
    toolbarAdaptive: false,
    toolbarSticky: true,
    height: 500,
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'fullsize'
    ],
    style: {
      background: '#ffffff',
      borderRadius: '4px'
    }
  }), [placeholder]);

  // Fetch blog data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Get role and userId from localStorage
      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const role = userDetails?.role;
      const userId = userDetails?.userId;

      let url = 'https://service.ireedindia.com/v1/blog?size=1005';

      // If not admin, add userId as param
      if (role !== 'admin' && userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
      }

      console.log('🔗 Fetching blogs from:', url);

      const response = await axios.get<BlogData>(url);
      setData(response.data);
    } catch (error) {
      message.error('Failed to fetch blog data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle file upload
  const handleFileUpload = async (info: any) => {
    if (info.file.status === 'uploading') {
      return;
    }

    if (info.file.status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`);
      setFileName(info.file.response.filename);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  // Custom file upload request
  const customUploadRequest = async ({ file, onSuccess, onError }: any) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('https://service.ireedindia.com/upload', formData);
      onSuccess(response.data, file);
    } catch (error) {
      console.error('File upload failed:', error);
      onError(error);
    }
  };

  // Handle blog form submit
  const handleSubmit = async (values: any) => {
    setLoading(true);

    // Get userId from localStorage
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userId = userDetails?.userId;

    const jsonData = {
      ...values,
      content,
      image: fileName,
      date: values.date ? values.date.format('YYYY-MM-DD') : '',
      faq,
      userId: userId, // Add userId to the payload
      // Enforce RBAC: only admin can set publish status from the form payload
      published: isAdmin ? isPublished : false // Non-admins cannot publish from form
    };

    console.log('🚀 Submitting blog with payload:', jsonData);

    try {
      let response;
      if (editingId) {
        response = await axios.patch(`https://service.ireedindia.com/v1/blog/${editingId}`, jsonData);
        message.success('Blog post updated successfully');
      } else {
        response = await axios.post('https://service.ireedindia.com/v1/blog', jsonData);
        message.success('Blog post created successfully');
      }

      if (response.status === 200 || response.status === 201) {
        fetchData();
        resetForm();
        setActiveTabKey('2'); // Switch to data tab
      }
    } catch (error) {
      message.error('Error saving blog post');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit blog
  const handleEdit = (id: string) => {
    const blogToEdit = data.blogs.find((blog) => blog._id === id);
    if (blogToEdit) {
      form.setFieldsValue({
        title: blogToEdit.title,
        fullTitle: blogToEdit.fullTitle,
        description: blogToEdit.description,
        date: blogToEdit.date ? dayjs(blogToEdit.date) : null,
        researchAssociate: blogToEdit.researchAssociate,
        meta: blogToEdit.meta,
        key: blogToEdit.key,
        slug: blogToEdit.slug,
        altTag: blogToEdit.altTag,
        Canonical: blogToEdit.Canonical
      });

      setContent(blogToEdit.content);
      setFileName(blogToEdit.image);
      setFaq(blogToEdit.faq || []);
      setIsPublished((blogToEdit.published ?? blogToEdit.isPublished) || false);
      setEditingId(id);
      setActiveTabKey('1'); // Switch to form tab

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle delete blog
  const handleDelete = async (id: string) => {
    // Enforce RBAC: only admin can delete
    if (!isAdmin) {
      message.error('You do not have permission to delete blogs');
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`https://service.ireedindia.com/v1/blog/${id}`);
      message.success('Blog deleted successfully');
      setData((prevData: BlogData) => ({
        ...prevData,
        blogs: prevData.blogs.filter((blog) => blog._id !== id)
      }));
    } catch (error) {
      message.error('Error deleting blog');
      console.error("Error deleting blog:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle publish/unpublish blog - Only for admin
  const handlePublish = async (id: string, currentStatus: boolean) => {
    // Enforce RBAC: only admin can toggle publish
    if (!isAdmin) {
      message.error('You do not have permission to change publish status');
      return;
    }
    setLoading(true);
    try {
      await axios.patch(`https://service.ireedindia.com/v1/blog/${id}`, {
        published: !currentStatus
      });
      message.success(`Blog ${!currentStatus ? 'published' : 'unpublished'} successfully`);

      // Update local state
      setData((prevData: BlogData) => ({
        ...prevData,
        blogs: prevData.blogs.map(blog =>
          blog._id === id ? { ...blog, published: !currentStatus } : blog
        )
      }));
    } catch (error) {
      message.error('Error updating publish status');
      console.error('Error updating publish status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    form.resetFields();
    setContent('');
    setFileName(null);
    setFaq([]);
    setIsPublished(false);
    setEditingId(null);
  };

  // FAQ handlers
  const handleAddFaq = () => {
    setFaq([...faq, { question: '', answer: '' }]);
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const updatedFaq = [...faq];
    updatedFaq[index][field] = value;
    setFaq(updatedFaq);
  };

  const handleRemoveFaq = (index: number) => {
    const updatedFaq = [...faq];
    updatedFaq.splice(index, 1);
    setFaq(updatedFaq);
  };

  // Toggle expanded row
  const toggleRowExpansion = (id: string) => {
    const currentExpandedRows = [...expandedRows];
    const index = currentExpandedRows.indexOf(id);

    if (index > -1) {
      currentExpandedRows.splice(index, 1);
    } else {
      currentExpandedRows.push(id);
    }

    setExpandedRows(currentExpandedRows);
  };

  // Table columns
  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '15%',
      ellipsis: true,
      render: (text: string) => <Tooltip title={text}><span>{text}</span></Tooltip>
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      ellipsis: true,
      render: (text: string) => <Tooltip title={text}><span>{text}</span></Tooltip>
    },
    {
      title: 'Canonical URL',
      dataIndex: 'Canonical',
      key: 'Canonical',
      width: '15%',
      ellipsis: true,
      render: (text: string) => <Tooltip title={text}><span>{text || 'N/A'}</span></Tooltip>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Research Associate',
      dataIndex: 'researchAssociate',
      key: 'researchAssociate',
      width: '15%',
      ellipsis: true
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: '10%',
      render: (image: string) =>
        image ? (
          <Tooltip title="View Image">
            <a
              href={`https://service.ireedindia.com/image/${image}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Badge count={<FileImageOutlined style={{ color: '#1890ff' }} />}>
                <img
                  src={`https://service.ireedindia.com/image/${image}`}
                  alt="Blog"
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '4px' }}
                />
              </Badge>
            </a>
          </Tooltip>
        ) : (
          <Text type="secondary">No image</Text>
        )
    },
    {
      title: 'Status',
      dataIndex: 'published',
      key: 'published',
      width: '10%',
      render: (published: boolean, record: any) => {
        const isPub = published ?? record.isPublished ?? false;
        return (
          <span style={{
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: isPub ? '#d1fae5' : '#fef3c7',
            color: isPub ? '#065f46' : '#92400e'
          }}>
            {isPub ? '✓ Published' : 'Draft'}
          </span>
        );
      },
    },
    // Admin-only columns
    ...(userRole === 'admin' ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: '15%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "userPostDataInfo",
        key: "createdAt",
        width: '12%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At",
        dataIndex: "userPostDataInfo",
        key: "updatedAt",
        width: '12%',
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
    ] : []),
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (text: any, record: any) => {
        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEdit(record._id)
          },
          ...(userRole === 'admin' ? [
            {
              key: 'publish',
              label: record.isPublished ? 'Unpublish' : 'Publish',
              onClick: () => handlePublish(record._id, record.isPublished || false)
            }
          ] : []),
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: expandedRows.includes(record._id) ? 'Hide Details' : 'View Details',
            onClick: () => toggleRowExpansion(record._id)
          },
          ...(userRole === 'admin' ? [
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete',
              danger: true,
              onClick: () => {
                // Handled by Popconfirm
              }
            }
          ] : [])
        ];

        return (
          <Space size="small" wrap>
            <Tooltip title="Edit">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record._id)}
              />
            </Tooltip>

            {userRole === 'admin' && (
              <Tooltip title={(record.published ?? record.isPublished) ? 'Unpublish' : 'Publish'}>
                <Button
                  type={(record.published ?? record.isPublished) ? "default" : "primary"}
                  size="small"
                  onClick={() => handlePublish(record._id, (record.published ?? record.isPublished) || false)}
                  style={{ backgroundColor: (record.published ?? record.isPublished) ? '#faad14' : '#52c41a', color: '#fff', borderColor: 'transparent' }}
                >
                  {(record.published ?? record.isPublished) ? 'P' : 'U'}
                </Button>
              </Tooltip>
            )}

            {userRole === 'admin' && (
              <Popconfirm
                title="Are you sure you want to delete this blog?"
                onConfirm={() => handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <Tooltip title="Delete">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
            )}

            <Tooltip title={expandedRows.includes(record._id) ? 'Hide Details' : 'View Details'}>
              <Button
                type="default"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => toggleRowExpansion(record._id)}
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  return (
    <Layout className="min-h-screen">
      <Header style={{ background: '#fff', padding: '0 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}>
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Title level={3} style={{ margin: 0, color: '#1890ff' }}>Blog Management System</Title>
        </div>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} type="card">
          <TabPane tab={<span><EditOutlined /> {editingId ? 'Edit Blog' : 'Create Blog'}</span>} key="1">
            <Card bordered={false} style={{ marginBottom: 24 }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ date: dayjs() }}
              >
                <Row gutter={16}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="title"
                      label="Title"
                      rules={[{ required: true, message: 'Please enter title' }]}
                    >
                      <Input placeholder="Enter blog title" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="fullTitle"
                      label="Full Title"
                      rules={[{ required: true, message: 'Please enter full title' }]}
                    >
                      <Input placeholder="Enter full title" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="slug"
                      label="Slug"
                      rules={[{ required: true, message: 'Please enter slug' }]}
                    >
                      <Input placeholder="Enter URL slug" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="altTag"
                      label="Alt Tag"
                      rules={[{ required: true, message: 'Please enter alt tag' }]}
                    >
                      <Input placeholder="Enter image alt tag" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[{ required: true, message: 'Please enter description' }]}
                >
                  <TextArea
                    placeholder="Enter blog description"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} lg={8}>
                    <Form.Item
                      name="date"
                      label="Publication Date"
                      rules={[{ required: true, message: 'Please select date' }]}
                    >
                      <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item
                      name="researchAssociate"
                      label="Research Associate"
                      rules={[{ required: true, message: 'Please enter research associate' }]}
                    >
                      <Input placeholder="Enter research associate name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Featured Image"
                      tooltip="Upload an image for your blog (PNG, JPG, WEBP only, max 5MB)"
                    //   rules={[{ required: true, message: 'Please enter Image' }]}
                    >
                      <Upload
                        name="file"
                        listType="picture"
                        maxCount={1}
                        customRequest={customUploadRequest}
                        onChange={handleFileUpload}
                        accept="image/png,image/jpg,image/webp"
                        showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                      >
                        <Button icon={<UploadOutlined />}>Upload Image</Button>
                      </Upload>
                      {fileName && (
                        <Text type="success" style={{ marginTop: 8, display: 'block' }}>
                          Image uploaded: {fileName}
                        </Text>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="meta"
                      label="Meta Description"
                      tooltip="SEO meta description"
                    >
                      <Input placeholder="Enter meta description" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="key"
                      label="Keywords"
                      tooltip="SEO keywords"
                    >
                      <Input placeholder="Enter keywords" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} lg={24}>
                    <Form.Item
                      name="Canonical"
                      label="Canonical URL"
                      tooltip="The canonical URL of the blog post"
                    >
                      <Input placeholder="Enter canonical URL" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Blog Content</Divider>
                <Form.Item
                  label="Content"
                  rules={[{ required: true, message: 'Please enter content' }]}
                >
                  <JoditEditor
                    ref={editor}
                    value={content}
                    config={config}
                    onBlur={(newContent: string) => setContent(newContent)}
                    onChange={(newContent: string) => { }}
                  />
                </Form.Item>

                <Divider orientation="left">
                  <Space>
                    <span>FAQs</span>
                    <Tooltip title="Add Frequently Asked Questions to improve SEO">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                </Divider>

                {faq.length > 0 && (
                  <Collapse
                    defaultActiveKey={['0']}
                    style={{ marginBottom: 24 }}
                    bordered={false}
                  >
                    {faq.map((faqItem, index) => (
                      <Panel
                        header={`FAQ #${index + 1}: ${faqItem.question.substring(0, 50) || 'New FAQ'}`}
                        key={index.toString()}
                        extra={
                          <Button
                            danger
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFaq(index);
                            }}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <Form.Item label="Question">
                          <Input
                            value={faqItem.question}
                            onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                            placeholder="Enter FAQ question"
                          />
                        </Form.Item>
                        <Form.Item label="Answer (HTML)">
                          <TextArea
                            value={faqItem.answer}
                            onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                            placeholder="Enter FAQ answer (HTML allowed)"
                            autoSize={{ minRows: 4, maxRows: 8 }}
                          />
                        </Form.Item>
                        <div style={{ marginTop: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                          <Paragraph strong>Preview:</Paragraph>
                          <div dangerouslySetInnerHTML={{ __html: faqItem.answer }} />
                        </div>
                      </Panel>
                    ))}
                  </Collapse>
                )}

                <Button
                  type="dashed"
                  onClick={handleAddFaq}
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 24, width: '100%' }}
                >
                  Add FAQ
                </Button>

                <Form.Item>
                  <Space size="middle">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                    >
                      {editingId ? 'Update Blog' : 'Create Blog'}
                    </Button>
                    <Button onClick={resetForm} icon={<ReloadOutlined />}>
                      Reset Form
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </TabPane>

          <TabPane tab={<span><SearchOutlined /> Manage Blogs</span>} key="2">
            <Card bordered={false}>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Title level={4} style={{ margin: 0 }}>All Blog Posts</Title>

                  {/* Admin-only: Publish Filter */}
                  {isAdmin && (
                    <Select
                      value={publishFilter}
                      onChange={(value) => setPublishFilter(value)}
                      style={{ width: 180 }}
                      placeholder="Filter by status"
                    >
                      <Option value="all">All Blogs</Option>
                      <Option value="published">Published</Option>
                      <Option value="unpublished">Unpublished</Option>
                    </Select>
                  )}
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setActiveTabKey('1');
                  }}
                >
                  Add New Blog
                </Button>
              </div>
              <Table
                dataSource={data.blogs.filter((blog) => {
                  // Apply publish filter (only if admin)
                  if (isAdmin && publishFilter !== "all") {
                    const isPub = blog.published ?? blog.isPublished ?? false;
                    if (publishFilter === "published" && !isPub) return false;
                    if (publishFilter === "unpublished" && isPub) return false;
                  }
                  return true;
                })}
                columns={columns}
                rowKey="_id"
                scroll={{ x: 1200 }}
                loading={loading}
                pagination={{ pageSize: 10 }}
                expandable={{
                  expandedRowKeys: expandedRows,
                  expandedRowRender: (record) => {
                    return (
                      <Card bordered={false} style={{ background: '#fafafa' }}>
                        <Tabs defaultActiveKey="1">
                          <TabPane tab="Content Preview" key="1">
                            <div style={{ padding: '12px', background: '#fff', borderRadius: '4px' }}>
                              <div dangerouslySetInnerHTML={{ __html: record.content }} />
                            </div>
                          </TabPane>
                          <TabPane tab="Details" key="2">
                            <Row gutter={[16, 16]}>
                              <Col xs={24} md={12}>
                                <Card size="small" title="Blog Details">
                                  <p><strong>Full Title:</strong> {record.fullTitle}</p>
                                  <p><strong>Description:</strong> {record.description}</p>
                                  <p><strong>Alt Tag:</strong> {record.altTag}</p>
                                  <p><strong>Meta:</strong> {record.meta}</p>
                                  <p><strong>Keywords:</strong> {record.key}</p>
                                </Card>
                              </Col>
                              <Col xs={24} md={12}>
                                <Card size="small" title="FAQs">
                                  {record.faq && record.faq.length > 0 ? (
                                    <Collapse bordered={false} style={{ background: '#fff' }}>
                                      {record.faq.map((faqItem, index) => (
                                        <Panel header={faqItem.question} key={index.toString()}>
                                          <div dangerouslySetInnerHTML={{ __html: faqItem.answer }} />
                                        </Panel>
                                      ))}
                                    </Collapse>
                                  ) : (
                                    <Text type="secondary">No FAQs available</Text>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                          </TabPane>
                        </Tabs>
                      </Card>
                    );
                  }
                }}
              />
            </Card>
          </TabPane>
        </Tabs>
      </Content>

      <Footer style={{ textAlign: 'center', background: '#f0f2f5', padding: '12px 50px' }}>
        Blog Management System © {new Date().getFullYear()}
      </Footer>
    </Layout>
  );
};

export default BlogManagement;